import { useSortable } from '@dnd-kit/sortable';
import { Dropdown, IconButton, Tooltip, useTranslationContext } from 'platform/components';
import { match } from 'ts-pattern';
import { useRef, useState } from 'react';
import { COLUMN_SEPARATOR_STYLE, HEADER_CELL_BORDER_BOTTOM, PINNED_INDICATOR_STYLE, PINNED_LEFT_EDGE_LINE_STYLE, PINNED_RIGHT_EDGE_LINE_STYLE, RESIZE_INDICATOR_LEFT_STYLE, RESIZE_INDICATOR_STYLE } from '../../../../constants/borderStyles';
import { DEFAULT_COL_WIDTH } from '../../../../constants/defaultColWidth';
import { useColumnMenuContext } from '../../../../context/ColumnMenuContext';
import { useIsMobileView } from '../../../../hooks/useIsMobileView';
import { useColumnResizingAllowed, useColumnSortMode } from '../../../../store/selectors';
import { selectHeaderCellOverlayState } from '../../../../store/selectors/selectHeaderCellOverlayState';
import { useGridSettingsSafe } from '../../../../store/selectors/useGridSettingsSafe';
import { useEagGridSelector } from '../../../../store/useEagGridSelector';
import type { EagColumn } from '../../../../types/EagColumn';
import type { EagColumnState } from '../../../../types/EagColumnState';
import { getPinnedEdgeShadowClass } from '../../../../utils/getPinnedEdgeShadowClass';
import { getRowPaddingClass, LineHeightToPx } from '../../../../utils/lineHeightToValue';
import { ColumnHeaderMenu } from './components/ColumnHeaderMenu/ColumnHeaderMenu';
import { computeHeaderCellLayout } from './utils/computeHeaderCellLayout';
import { computeHeaderCellPinnedStyle } from './utils/computeHeaderCellPinnedStyle';
export type HeaderCellProps = {
  column: EagColumn;
  columnState: EagColumnState;
  colIndex: number;
  onSort: (key: string) => void;
  onResizeStart: (key: string, x: number, colIndex: number, resizeFromLeft?: boolean) => void;
  pinnedLeftOffset?: number;
  pinnedRightOffset?: number;
  // Pinning edge shadow — true on the boundary column of each pinned group
  isLastPinnedLeft?: boolean;
  isFirstPinnedRight?: boolean;
  /** Sticky top offset in px. 0 when no group header row is shown; GROUP_HEADER_ROW_HEIGHT when visible. */
  stickyTop: number;
};
export function HeaderCell(props: HeaderCellProps) {
  // Composite selector: 5 drag/resize fields → 1 subscription instead of 5.
  const overlay = useEagGridSelector(selectHeaderCellOverlayState);
  const gridSettings = useGridSettingsSafe();
  const heightPx = LineHeightToPx[gridSettings.headerHeight];
  const paddingClass = getRowPaddingClass[gridSettings.headerHeight];

  // Behavior flags
  const columnResizingAllowed = useColumnResizingAllowed();
  const columnSortMode = useColumnSortMode();

  // Column menu state and callbacks — provided via ColumnMenuContext by EagGridTable.
  // No prop drilling through HeaderRow.
  const columnMenu = useColumnMenuContext();
  const isMobile = useIsMobileView();
  const t = useTranslationContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const thRef = useRef<HTMLTableCellElement | null>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging
  } = useSortable({
    id: props.column.key,
    // Disable animated transforms — body cells cannot follow the header transform,
    // so we use DragOverlay for the full-column visual instead.
    animateLayoutChanges: () => false
  });
  const width = props.columnState.width ?? DEFAULT_COL_WIDTH;
  const cellLayout = computeHeaderCellLayout({
    column: props.column,
    columnState: props.columnState,
    width,
    headerHeight: gridSettings.headerHeight,
    showColumnMenu: columnMenu.showColumnMenu,
    columnSortMode,
    isMobile,
    filterState: columnMenu.filterState
  });
  const useSmallButtons = cellLayout.useSmallButtons;
  const showMenuButton = cellLayout.showMenuButton;
  const showSortButton = cellLayout.showSortButton;
  const textPaddingRight = cellLayout.textPaddingRight;
  const isColumnFilterActive = cellLayout.isColumnFilterActive;
  const menuPriority = cellLayout.menuPriority;
  const columnHasMenuContent = cellLayout.columnHasMenuContent;
  const isPinned = props.columnState.pinned === 'LEFT' || props.columnState.pinned === 'RIGHT';
  const isEdge = (props.isLastPinnedLeft ?? false) || (props.isFirstPinnedRight ?? false);
  const shadowClass = getPinnedEdgeShadowClass(props.columnState.pinned, isEdge);

  // stickyTop is 0 normally, GROUP_HEADER_ROW_HEIGHT when the group header row is visible —
  // so the main header row sticks below the group row instead of overlapping it.
  const pinnedStyle = computeHeaderCellPinnedStyle({
    pinned: props.columnState.pinned,
    stickyTop: props.stickyTop,
    pinnedLeftOffset: props.pinnedLeftOffset,
    pinnedRightOffset: props.pinnedRightOffset
  });
  const sortIconValue = match(props.columnState.sort).with('ASC', () => 'navigation/arrow_upward' as const).with('DESC', () => 'navigation/arrow_downward' as const).otherwise(() => 'navigation/unfold_more' as const);
  const sortPriority = match(props.columnState.sort).with('ASC', 'DESC', () => 'primary' as const).otherwise(() => undefined);

  // Desktop: buttons always visible if sort or filter is active; otherwise show only on hover
  const isSortActive = props.columnState.sort != null;
  const isActiveState = isSortActive || isColumnFilterActive;
  const desktopVisibilityClass = isActiveState ? 'opacity-100' : 'opacity-0 group-hover/header:opacity-100 transition-opacity duration-150';
  const ariaSortValue = match(props.columnState.sort).with('ASC', () => 'ascending' as const).with('DESC', () => 'descending' as const).otherwise(() => props.column.sortable ? 'none' as const : undefined);
  const handleSortClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    props.onSort(props.column.key);
  };
  const isRightPinned = props.columnState.pinned === 'RIGHT';
  const handleResizePointerDown = (event: React.PointerEvent) => {
    event.stopPropagation();
    props.onResizeStart(props.column.key, event.clientX, props.colIndex, isRightPinned);
  };
  const handleMenuButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };
  const isBeingResized = overlay.isResizing && overlay.resizingColumn === props.column.key;

  // Pinned cells get subtle gray background; drop-target overrides both
  const bgClass = match({
    isDropTarget: overlay.overColumnId === props.column.key && !(isDragging || overlay.activeDragId === props.column.key),
    isPinned
  }).with({
    isDropTarget: true
  }, () => 'bg-blue-50').with({
    isPinned: true
  }, () => 'bg-gray-50').otherwise(() => 'bg-white');
  const thStyle = {
    width,
    maxWidth: width,
    height: heightPx,
    ...pinnedStyle,
    ...HEADER_CELL_BORDER_BOTTOM
  };
  const isDragSource = isDragging || overlay.activeDragId === props.column.key;
  const isDropTarget = overlay.overColumnId === props.column.key && !isDragSource;
  const isDropLeft = isDropTarget && overlay.dropSide === 'left';
  const isDropRight = isDropTarget && overlay.dropSide === 'right';
  const dragStyle = {
    opacity: isDragSource ? 0.3 : 1
  };
  return <th ref={node => {
    setNodeRef(node);
    thRef.current = node;
  }} className={`group/header relative text-xs font-semibold text-gray-600 text-ellipsis whitespace-nowrap p-0 select-none ${bgClass} ${shadowClass}`} scope="col" style={{
    ...thStyle,
    ...dragStyle
  }} {...attributes} role="columnheader" aria-sort={ariaSortValue} data-column={props.column.key}>
      {/* Text area — drag listeners for column reorder, NO sort-on-click */}
      <div className={`flex items-center h-full min-w-0 overflow-hidden ${paddingClass}`} style={{
      paddingRight: textPaddingRight
    }} {...listeners}>
        <span className="min-w-0 flex-1 overflow-hidden">
          <Tooltip label={props.column.headerName}>
            <span className="truncate block">{props.column.headerName}</span>
          </Tooltip>
        </span>
      </div>

      {/* Action buttons: absolute, right-aligned, vertically centered, OUTSIDE drag listener zone */}
      {/* bgClass matches the cell background so text cannot bleed through in narrow columns */}
      <div className={`absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1 pr-1 ${bgClass} ${!isMobile ? desktopVisibilityClass : ''}`}>
        {isMobile ?
      // Mobile: single always-visible hamburger opens ColumnHeaderMenu
      <>
            {columnMenu.showColumnMenu && columnHasMenuContent && <Dropdown isOpen={isMenuOpen} onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)} placement="bottom-end" onButtonClick={handleMenuButtonClick} isFocusManagementDisabled isKeyboardNavigationDisabled dropdownControl={<IconButton icon="navigation/menu" size={useSmallButtons ? 'small' : 'default'} aria-label={t('page.dataGrid.column.optionsAriaLabel', {
          columnName: props.column.headerName
        })} />}>
                <ColumnHeaderMenu column={props.column} columnPinned={props.columnState.pinned} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
              </Dropdown>}
          </> :
      // Desktop: sort button + menu button
      <>
            {showSortButton && <IconButton icon={sortIconValue} size={useSmallButtons ? 'small' : 'default'} onClick={handleSortClick} isDisabled={!props.column.sortable} priority={sortPriority} aria-label={t('page.dataGrid.column.sortAriaLabel', {
          columnName: props.column.headerName
        })} />}
            {showMenuButton && columnMenu.showColumnMenu && <Dropdown isOpen={isMenuOpen} onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)} placement="bottom-end" onButtonClick={handleMenuButtonClick} isFocusManagementDisabled isKeyboardNavigationDisabled dropdownControl={<IconButton icon="navigation/menu" size={useSmallButtons ? 'small' : 'default'} priority={menuPriority} aria-label={t('page.dataGrid.column.optionsAriaLabel', {
          columnName: props.column.headerName
        })} />}>
                <ColumnHeaderMenu column={props.column} columnPinned={props.columnState.pinned} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
              </Dropdown>}
          </>}
      </div>

      {/* Pinned indicator — orange strip at top edge, visible on all pinned columns */}
      {isPinned && <span style={PINNED_INDICATOR_STYLE} />}

      {/* Pinned edge line: full-height separator replacing the 50%-height column separator */}
      {!isBeingResized && props.columnState.pinned === 'LEFT' && <span style={PINNED_LEFT_EDGE_LINE_STYLE} />}
      {!isBeingResized && props.columnState.pinned === 'RIGHT' && <span style={PINNED_RIGHT_EDGE_LINE_STYLE} />}
      {/* Resize indicator / separators / drop indicators */}
      {/* Right-pinned columns use a left-side indicator so it aligns with the left-side handle */}
      {isBeingResized && <span style={isRightPinned ? RESIZE_INDICATOR_LEFT_STYLE : RESIZE_INDICATOR_STYLE} />}
      {/* Column separator: always visible in header cells (ignores verticalSeparators setting),
          only on non-pinned cells (pinned cells use the edge line above) */}
      {!isBeingResized && !isPinned && <span style={COLUMN_SEPARATOR_STYLE} />}
      {columnResizingAllowed && props.column.resizable !== false && !isBeingResized && <span className={isRightPinned ?
    // Left-side handle for right-pinned columns — more accessible when column is at far right
    'absolute -left-[2px] top-0 h-full w-[5px] cursor-col-resize z-(--z-content-loader) flex items-stretch justify-center' : 'absolute -right-[2px] top-0 h-full w-[5px] cursor-col-resize z-(--z-content-loader) flex items-stretch justify-center'} onPointerDown={handleResizePointerDown}>
          <span className="w-px h-full hover:bg-blue-400 pointer-events-none" />
        </span>}
      {isDropLeft && <span className="absolute left-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />}
      {isDropRight && <span className="absolute right-0 top-0 h-full w-px bg-blue-500 z-(--z-page-navigation)" />}
    </th>;
}